import { createApi } from '@reduxjs/toolkit/query/react'
import dayjs from 'dayjs'

import { NotificationController } from 'core-components/library/popup'
import { IRole } from 'core-components/sitebuilder/models/IRole'
import {
  setRoles,
  setSitePages,
  setWidgets,
} from 'core-components/sitebuilder/store/siteBuilderSlice'
import { saveToCache } from 'src/core/classes/cache/Cache'
import StorageKeys from 'src/core/classes/cache/StorageKeys'
import { getTokens } from 'src/core/classes/helpers/StorageHelper'
import { proxyIdentity, proxySiteBuilder } from 'src/core/classes/helpers/UrlHelper'
import { ADMIN } from 'src/core/constants/roles'
import { RootState } from 'src/core/store'
import { setUser as setCoreUser } from 'src/core/store/appSlice'
import { downloadFile } from 'src/core/utils/functions'
import Logger from 'src/core/utils/Logger'

import { getCustomQuery } from '../helpers/getCustomQuery'

export const roleApi = createApi({
  reducerPath: '@app/api/rolesApi',
  baseQuery: getCustomQuery({
    storageKey: StorageKeys.roles,
    baseUrl: proxyIdentity('/mbe/api/v2'),
    cachedUrl: [{ url: '/app/roles', method: 'GET' }],
  }),
  endpoints: (builder) => ({
    fetchAllRoles: builder.query<IRole[], void>({
      query: () => '/app/roles',
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled
          const preparedItems = data?.map((item) => prepareRole(item))
          dispatch(setRoles(preparedItems))
          saveToCache(StorageKeys.roles, preparedItems, true)
        } catch (err) {
          Logger.error('Failed to fetch roles:', err)
        }
      },
    }),
    createRole: builder.mutation<IRole, IRole>({
      query: (role) => ({
        url: `/app/roles`,
        method: 'POST',
        body: getPayloadRole(role),
      }),
      async onQueryStarted(role, { queryFulfilled, dispatch, getState }) {
        try {
          await queryFulfilled
          const roles = (getState() as unknown as RootState).siteBuilder.roles
          const newRoles = [...roles, role]
          dispatch(setRoles(newRoles))
          saveToCache(StorageKeys.roles, newRoles)
        } catch (err) {
          Logger.error('Failed to create role:', err)
        }
      },
    }),
    updateRole: builder.mutation<IRole, { id: string; role: IRole }>({
      query: ({ id, role }) => ({
        url: `/app/roles/${id}`,
        method: 'PUT',
        body: getPayloadRole(role),
      }),
      async onQueryStarted({ id }, { queryFulfilled, dispatch, getState }) {
        try {
          const { data: updatedRole } = await queryFulfilled
          const preparedRole = prepareRole(updatedRole)
          const roles = (getState() as unknown as RootState).siteBuilder.roles
          const newRoles = roles.map((r) => (r.id === id ? preparedRole : r))
          dispatch(setRoles(newRoles))
          saveToCache(StorageKeys.roles, newRoles)
        } catch (err) {
          Logger.error('Failed to update role:', err)
        }
      },
    }),
    deleteRole: builder.mutation<void, string>({
      query: (id) => ({
        url: `/app/roles/${id}`,
        method: 'DELETE',
        body: {},
      }),
      async onQueryStarted(id, { queryFulfilled, dispatch, getState }) {
        const state = getState() as unknown as RootState
        const roles = state.siteBuilder.roles
        const sitePages = state.siteBuilder.sitePages
        const widgets = state.siteBuilder.widgets
        const user = state.app.user

        try {
          if (id === ADMIN) {
            return NotificationController.showError({ message: `Trying to delete "${ADMIN}" role` })
          }

          await queryFulfilled

          const newRoles = roles.filter((role) => role.id !== id)

          const newSitePages = sitePages.map((page) => ({
            ...page,
            roleIds: page.roleIds.filter((roleId) => roleId !== id),
          }))

          const newWidgets = widgets.map((widget) => ({
            ...widget,
            roleIds: widget.roleIds.filter((roleId) => roleId !== id),
          }))

          const newUser = {
            ...user,
            roleIds: user.roleIds.filter((roleId: string) => roleId !== id),
          }

          dispatch(setRoles(newRoles))
          dispatch(setSitePages(newSitePages))
          dispatch(setWidgets(newWidgets))
          dispatch(setCoreUser(newUser))

          await saveToCache(StorageKeys.roles, newRoles)
        } catch (err: unknown) {
          NotificationController.showError({
            message: (err as Error).message,
          })
        }
      },
    }),
    getAppRoles: builder.query<IRole[], string>({
      query: (appId) => `/apps/${appId}/roles`,
      transformResponse: (data: unknown): IRole[] => {
        if (!Array.isArray(data)) return []
        return data.map(prepareRole)
      },
    }),
    exportRoles: builder.mutation<void, string>({
      queryFn: async (appId) => {
        try {
          const [token] = getTokens()
          if (token) {
            downloadFile({
              fileName: `${dayjs().unix()}-${appId}-Roles.xlsx`,
              requestData: {
                url: proxySiteBuilder('/mbe/api/v2/export/site-pages/roles'),
                method: 'POST',
                authorization: {
                  authParams: token,
                },
              },
            })
          }
          return { data: undefined }
        } catch (err) {
          Logger.error('Failed to export roles:', err)
          return { error: err as any }
        }
      },
    }),
  }),
})

export const {
  useFetchAllRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetAppRolesQuery,
  useExportRolesMutation,
} = roleApi

const prepareRole = (role: IRole) => {
  const format = Object.keys(role)
  const lowerFirstLetter = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)

  const newRole = {}
  format.map((field) => {
    let value = role[field]
    if (field === 'Rules' && typeof value !== 'object') {
      value = value ? JSON.parse(value) : value
    }
    newRole[lowerFirstLetter(field)] = value
  })

  return newRole as IRole
}

const getPayloadRole = (role: IRole) => {
  const format = Object.keys(role) as (keyof IRole)[]
  const upperFirstLetter = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

  return format.reduce((newRole, field) => {
    let value = role[field]
    if (field === 'rules') {
      value = value ? JSON.stringify(value) : value
    }
    newRole[upperFirstLetter(field)] = value
    return newRole
  }, {})
}
